// =============================================================================
// Wake-word activation — browser-only
// =============================================================================
// Two paths to open a Jarvis voice session from the Command Center:
//   1. Mic button click — explicit, always works.
//   2. Passive listening (toggle in Settings) — opens a wake-word stream:
//      - "Hey Jarvis" via SpeechRecognition phrase match
//      - Two claps ~200-400ms apart via AudioWorklet transient detection
//
// On wake, this dispatches a "jarvis-wake" CustomEvent. The Command Center
// listens for it and opens the chat (or, later, a WebRTC voice session via
// `/api/voice/web-token` once the backend route lands).
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { Mic, MicOff } from 'lucide-react'
import { usePersona } from '@/lib/personas'

const WAKE_LISTEN_KEY = 'jarvis.wakeListen'

interface SpeechResultEvent {
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal?: boolean }>
}
interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  continuous: boolean
  start: () => void
  stop: () => void
  onresult: ((event: SpeechResultEvent) => void) | null
  onerror: ((event: unknown) => void) | null
  onend: ((event: unknown) => void) | null
}

const WAKE_PATTERNS: Record<string, RegExp[]> = {
  jarvis: [/hey,?\s*jarvis/i, /\bjarvis\b/i],
  ultron: [/hey,?\s*ultron/i, /\bultron\b/i],
  ares: [/hey,?\s*ares/i, /\bares\b/i],
}

export function WakeWord() {
  const { persona } = usePersona()
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WAKE_LISTEN_KEY) === '1'
    } catch {
      return false
    }
  })
  const [listening, setListening] = useState(false)
  const [supported, setSupported] = useState<boolean | null>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const clapTimestampsRef = useRef<number[]>([])

  useEffect(() => {
    try {
      localStorage.setItem(WAKE_LISTEN_KEY, enabled ? '1' : '0')
    } catch {
      // ignore
    }
  }, [enabled])

  // Capability detection runs once on mount
  useEffect(() => {
    const w = window as unknown as {
      SpeechRecognition?: unknown
      webkitSpeechRecognition?: unknown
    }
    setSupported(Boolean(w.SpeechRecognition || w.webkitSpeechRecognition))
  }, [])

  const fireWake = (reason: 'phrase' | 'clap' | 'button') => {
    window.dispatchEvent(new CustomEvent('jarvis-wake', { detail: { reason, persona: persona.id } }))
  }

  useEffect(() => {
    if (!enabled || supported !== true) {
      // Stop any active streams
      try {
        recognitionRef.current?.stop()
      } catch {
        // ignore
      }
      recognitionRef.current = null
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => undefined)
        audioCtxRef.current = null
      }
      setListening(false)
      return
    }

    const w = window as unknown as {
      SpeechRecognition?: new () => SpeechRecognitionLike
      webkitSpeechRecognition?: new () => SpeechRecognitionLike
    }
    const SR = w.SpeechRecognition ?? w.webkitSpeechRecognition
    if (!SR) return

    let cancelled = false

    // -- Wake-phrase loop ----------------------------------------------------
    const startRecognition = () => {
      if (cancelled) return
      const recognition = new SR()
      recognition.lang = 'en-US'
      recognition.interimResults = true
      recognition.continuous = true
      recognitionRef.current = recognition

      recognition.onresult = (event: SpeechResultEvent) => {
        for (let i = 0; i < event.results.length; i += 1) {
          const t = event.results[i]?.[0]?.transcript ?? ''
          if (!t) continue
          const patterns = WAKE_PATTERNS[persona.id] ?? WAKE_PATTERNS.jarvis
          if (patterns.some((p) => p.test(t))) {
            fireWake('phrase')
            return
          }
        }
      }
      recognition.onerror = () => {
        // restart on next tick; some browsers throw transient no-speech errors
        setTimeout(() => {
          if (!cancelled) startRecognition()
        }, 800)
      }
      recognition.onend = () => {
        setTimeout(() => {
          if (!cancelled) startRecognition()
        }, 200)
      }
      try {
        recognition.start()
        setListening(true)
      } catch {
        // already started — ignore
      }
    }

    startRecognition()

    // -- Clap detector -------------------------------------------------------
    // Uses analyser node + RMS spike detection. Cheap and good enough for
    // double-clap; not perfect — true wake-word ML would need a model.
    let analyser: AnalyserNode | null = null
    // Web Audio's getByteFrequencyData wants Uint8Array<ArrayBuffer> specifically
    // since TS 5.7 tightened typed-array generics — be explicit so the bare
    // `Uint8Array` doesn't default to ArrayBufferLike.
    let dataArr: Uint8Array<ArrayBuffer> | null = null
    let rafHandle = 0
    let streamRef: MediaStream | null = null

    const startClapWatch = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef = stream
        const ctx = new (window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
        audioCtxRef.current = ctx
        const src = ctx.createMediaStreamSource(stream)
        analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        src.connect(analyser)
        dataArr = new Uint8Array(analyser.frequencyBinCount)

        const tick = () => {
          if (cancelled || !analyser || !dataArr) return
          analyser.getByteFrequencyData(dataArr)
          // Sum high-frequency bins — claps are broadband transients with
          // strong high-frequency content; speech is bass-heavy.
          let highEnergy = 0
          const start = Math.floor(dataArr.length * 0.4)
          for (let i = start; i < dataArr.length; i += 1) {
            highEnergy += dataArr[i] as number
          }
          const avg = highEnergy / (dataArr.length - start)
          const now = performance.now()
          if (avg > 130) {
            // Treat as transient; throttle within a 100ms window
            const last = clapTimestampsRef.current[clapTimestampsRef.current.length - 1]
            if (!last || now - last > 100) {
              clapTimestampsRef.current.push(now)
              // Keep only recent claps
              clapTimestampsRef.current = clapTimestampsRef.current.filter(
                (t) => now - t < 800,
              )
              if (clapTimestampsRef.current.length >= 2) {
                const [a, b] = clapTimestampsRef.current.slice(-2)
                if (b - a > 150 && b - a < 500) {
                  clapTimestampsRef.current = []
                  fireWake('clap')
                }
              }
            }
          }
          rafHandle = requestAnimationFrame(tick)
        }
        tick()
      } catch {
        // Mic permission denied — wake-phrase still works via SpeechRecognition
      }
    }

    startClapWatch()

    return () => {
      cancelled = true
      try {
        recognitionRef.current?.stop()
      } catch {
        // ignore
      }
      recognitionRef.current = null
      if (rafHandle) cancelAnimationFrame(rafHandle)
      if (streamRef) streamRef.getTracks().forEach((t) => t.stop())
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => undefined)
        audioCtxRef.current = null
      }
      setListening(false)
    }
  }, [enabled, persona.id, supported])

  if (supported === false) {
    // Compact disabled state — explicit so users know why nothing happens
    return (
      <button
        disabled
        title="Wake word not supported in this browser"
        className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-jarvis-border opacity-40 cursor-not-allowed"
      >
        <MicOff size={12} /> Wake word unavailable
      </button>
    )
  }

  return (
    <button
      onClick={() => setEnabled((v) => !v)}
      title={
        enabled
          ? `Listening for "Hey ${persona.wordmark}" or two claps`
          : `Enable "Hey ${persona.wordmark}" + clap-clap wake`
      }
      className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors ${
        enabled
          ? 'border-jarvis-blue/60 bg-jarvis-blue/10 text-jarvis-blue'
          : 'border-jarvis-border text-jarvis-muted hover:text-jarvis-text'
      }`}
    >
      {enabled ? (
        <Mic size={12} className={listening ? 'animate-pulse' : ''} />
      ) : (
        <MicOff size={12} />
      )}
      {enabled ? `Listening — Hey ${persona.wordmark}` : 'Wake word off'}
    </button>
  )
}
